import React, { useState } from 'react';
import { useController } from 'react-hook-form';
import { graphql, useRelayEnvironment } from 'react-relay';
import { useTheme } from 'styled-components';
import { useFetchQuery } from '@sparknz/journeys-common';
import {
  Box,
  Button,
  StoreFinder,
  Suggestion,
  Text,
} from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { ShopAddressFieldQuery } from '__generated__/ShopAddressFieldQuery.graphql';
import { PRODUCT_DETAILS_QUERY_PARAMS } from 'journey/productDetails/constants/query.constant';
import { useQuery } from 'shared/hooks';

const MAX_LENGTH_ERROR_MESSAGE = 'Please enter no more than 100 letters';

const ShopAddressCheckerQuery = graphql`
  query ShopAddressCheckerQuery($partialAddress: String!) {
    location {
      address {
        suggestions(partialAddress: $partialAddress) {
          value: elid
          label
        }
      }
    }
  }
`;

const infoLayerContentKey = 'productDetails.address.cantFind';
export default function ShopAddressField() {
  const environment = useRelayEnvironment();
  const [suggestions, setSuggestions] = useState([]);
  const { fetch: fetchSuggestions } = useFetchQuery<ShopAddressFieldQuery>(
    ShopAddressCheckerQuery,
    environment,
  );
  const {
    palette: {
      baseColors: { marangai },
    },
  } = useTheme();

  const { replaceQuery, query } = useQuery();

  const { field: addressField, fieldState: addressFieldState } = useController({
    name: 'queryElidAddress',
    shouldUnregister: true,
    rules: {
      maxLength: {
        value: 100,
        message: MAX_LENGTH_ERROR_MESSAGE,
      },
    },
    defaultValue: '',
  });
  const { field: elidField, fieldState: elidFieldState } = useController({
    name: 'queryElid',
    shouldUnregister: true,
  });

  const openInfo = () => {
    replaceQuery({
      ...query,
      [PRODUCT_DETAILS_QUERY_PARAMS.INFO_LAYER]: infoLayerContentKey,
    });
    trackCustomLink(`I can't find my address`);
  };
  const onChange = (newValue: string) => {
    addressField.onChange(newValue);
    elidField.onChange(null);
  };

  const onSuggestionsFetchRequested = (partialAddress: string) => {
    const isPartialValidForSearch =
      (partialAddress.match(/[A-Za-z]/g) || []).length > 2;
    if (isPartialValidForSearch && fetchSuggestions) {
      fetchSuggestions({ partialAddress })
        .then(
          ({
            location: {
              address: { suggestions: matches },
            },
          }) => {
            setSuggestions(matches as Suggestion[]);
          },
        )
        .catch(() => {
          setSuggestions([]);
        });
    } else {
      setSuggestions([]);
    }
  };

  const handleSelection = (address: Suggestion) => {
    addressField.onChange(address.label);
    elidField.onChange(address.value);
  };

  const isError = elidFieldState?.invalid && elidFieldState?.isDirty;

  const errorText = isError
    ? addressFieldState?.error?.message || 'We could not find this address'
    : null;

  return (
    <>
      <Box mb="s1">
        <Text variant="bodySDemi">Enter your address</Text>
        <StoreFinder
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={null}
          onClearInput={() => onChange('')}
          onCurrentLocationDetected={null}
          placeholder="Enter your address"
          value={addressField.value}
          onChange={onChange}
          disabled={false}
          onSelect={(value) => {
            handleSelection(value);
          }}
          detectLocation={false}
          displayIcon={false}
          rounded={false}
        />
        {isError ? (
          <Text variant="footnoteDemi" color={marangai}>
            {errorText}
          </Text>
        ) : null}
      </Box>
      <Button variant="textbutton" type="button" onClick={openInfo}>
        I can&#39;t find my address
      </Button>
    </>
  );
}
